import MailIcon from '@mui/icons-material/Mail'
import PinDropIcon from '@mui/icons-material/PinDrop'
import StoreIcon from '@mui/icons-material/Store'
import PaymentsIcon from '@mui/icons-material/Payments'
import { Avatar, Box, Grid, Link, Tab, Typography, styled } from '@mui/material'
import LoginLeft from 'assets/images/login-left.png'
import { AuthLayout } from 'components'
import { ADDRESS, LOGIN_ITEMS, MESSAGES, ROUTES_PATH, WELCOME_DESCRIPTION, WELCOME_TITLE } from 'config'
import { color } from 'config/color'
import { Tabs, tabSx } from 'containers/Billing'
import { useAuth } from 'context'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { Item, LoginForm, PayForm } from './component'
import { useSnackbar } from 'notistack'


export const SignIn: React.FC = () => {
  const { handleLogOut } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<number>(0)
  const isMobile = useMobileBreakpoints()
  const handleTabChange = (_e: any, newActiveTab: number): void => {
    setActiveTab(newActiveTab)
  }
  useEffect(() => {
    void handleLogOut()
    navigate(ROUTES_PATH.SIGNIN)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  return (
    <AuthLayout
      title={WELCOME_TITLE}
      desc={WELCOME_DESCRIPTION}
      isAuthPage={true}
    >
      <AuthWrapper isAuthPage={true}>
        <Grid container>
          <Grid item sm={0} md={6}>
            <Box
              sx={{
                backgroundSize: 'cover',
                backgroundImage: `url("${LoginLeft}")`,
                height: '100%'
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Tabs variant='fullWidth' value={activeTab} onChange={handleTabChange} TabIndicatorProps={{ sx: { background: color.primary[500], height: 5 } }}>
              <Tab key={0} label="Pay your Bill" sx={tabSx} />
              <Tab key={1} label="Login or Register" sx={tabSx} />
            </Tabs>
            <AuthMain>
              {activeTab === 0 && <PayForm handleTabChange={handleTabChange} />}
              {activeTab === 1 && <LoginForm />}
            </AuthMain>
          </Grid>
          {!isMobile && <Box width="100%" mt="60px">
            <Typography color={color.grey[300]} align="center" fontSize={24} mb={3}>How can we help you?</Typography>

            <Box display="flex" flexWrap="wrap" justifyContent="space-between">
              {LOGIN_ITEMS.map((item, index) => (
                <Item key={index} Icon={item.icon} title={item.title} route={item.link} />
              ))}
            </Box>
          </Box>}
        </Grid>
      </AuthWrapper>
      {isMobile && <Box my={3}>
        <Typography color={color.grey[300]} align="center" fontSize={24} mb={3}>How can we help you?</Typography>
        <Box alignItems="center" justifyContent="center">
          {LOGIN_ITEMS.map((item) => {
            return (
              <Item
                key={item.title}
                Icon={item.icon}
                title={item.title}
                route={item.link}
              />
            )
          })}
        </Box>
      </Box>}
      {isMobile
        ? <Box display="flex" flexDirection="column">
          <Box bgcolor={color.background[950]} px={3} py={4}>
            <OtherPaymentOptionsContent />
          </Box>
        </Box>
        : <Box display="flex" gap={2} bgcolor={color.background[950]} px={3} py={4} mt={5} borderRadius={1}>
          <Box width="100%">
            <OtherPaymentOptionsContent />
          </Box>
        </Box>}
    </AuthLayout>
  )
}


export const OtherPaymentOptionsContent: React.FC = () => {
  const isMobile = useMobileBreakpoints()
  const { enqueueSnackbar } = useSnackbar()

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(ADDRESS)
      enqueueSnackbar(MESSAGES.CLIPBOARD_COPY, {
        variant: 'success'
      })
    } catch (err) {
      enqueueSnackbar(MESSAGES.CLIPBOARD_COPY_ERROR, {
        variant: 'error'
      })
    }
  }

  return <>
    <Box display="flex" alignItems="center" justifyContent="center" gap="5px">
      <PinDropIcon sx={{ color: color.primary[600] }} />
      <Typography color={color.white} fontSize={18} fontWeight={400}>Other Payment Options</Typography>
    </Box>
    <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap="20px" mt="30px">
      <Box display="flex" flexDirection='column' gap={isMobile ? '10px' : '60px'} alignItems="start" width={isMobile ? 'auto' : '33%'} border={`solid 1px ${color.background[800]}`} borderRadius="4px" p="20px">
        <Avatar sx={{ background: color.background[800], p: 2 }}>
          <MailIcon
            sx={{ color: color.secondary[100], fontSize: 40 }}
          />
        </Avatar>
        <Box flexGrow={1}>
          <Typography color={color.white} fontSize={18} fontWeight={400}>Mail</Typography>
          <Typography color={color.white} fontSize={16}>Mail check or money order to {ADDRESS}</Typography>
        </Box>
        <Box mt="40px">
          <Link style={{ cursor: 'pointer', color: color.secondary[100], textDecorationColor: color.secondary[100] }} onClick={handleCopy}>Copy Address to Clipboard</Link>
        </Box>
      </Box>
      <Box display="flex" flexDirection='column' gap={isMobile ? '10px' : '60px'} alignItems="start" width={isMobile ? 'auto' : '33%'} border={`solid 1px ${color.background[800]}`} borderRadius="4px" p="20px">
        <Avatar sx={{ background: color.background[800], p: 2 }}>
          <StoreIcon
            sx={{ color: color.secondary[100], fontSize: 40 }}
          />
        </Avatar>
        <Box flexGrow={1}>
          <Typography color={color.white} fontSize={18} fontWeight={400}>Walk-in</Typography>
          <Typography color={color.white} fontSize={16}>Delco Water customers have the ability to walk in to our office to pay their bills in person.</Typography>
        </Box>
        <Box mt="40px">
          <Link href="https://www.google.com.br/maps/dir//Del-Co+Water+Co+Inc,+6658+Olentangy+River+Rd,+Delaware,+OH+43015,+United+States/@40.202112,-83.0602207,17z/data=!4m9!4m8!1m0!1m5!1m1!1s0x8838f1eab36cd17f:0x73fee48e3b7537be!2m2!1d-83.0576458!2d40.2021079!3e0?entry=ttu" target="_blank" rel="noopener" style={{ cursor: 'pointer', color: color.secondary[100], textDecorationColor: color.secondary[100] }}>Get Directions</Link>
        </Box>

      </Box>
      <Box display="flex" flexDirection='column' gap={isMobile ? '10px' : '60px'} alignItems="start" width={isMobile ? 'auto' : '33%'} border={`solid 1px ${color.background[800]}`} borderRadius="4px" p="20px">
        <Avatar sx={{ background: color.background[800], p: 2 }}>
          <PaymentsIcon
            sx={{ color: color.secondary[100], fontSize: 40 }}
          />
        </Avatar>
        <Box flexGrow={1}>
          <Typography color={color.white} fontSize={18} fontWeight={400}>Cash and Check</Typography>
          <Typography color={color.white} fontSize={16}>Payments Are accepted at any local First Commonwealth Bank, Please have your bill stub when making payments at the Bank.</Typography>
        </Box>
        <Box mt="40px">
          <Link href="https://www.fcbanking.com/branch-locations/" target="_blank" rel="noopener" style={{ cursor: 'pointer', color: color.secondary[100], textDecorationColor: color.secondary[100] }}>Find Locations Near</Link>
        </Box>

      </Box>
    </Box >
  </>
}
export const AuthWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAuthPage'
})<{ isAuthPage?: boolean }>(({ isAuthPage }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  margin: (isAuthPage ?? false) ? '40px 0' : '0'
}))

export const AuthMain = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isRounded'
})<{ isRounded?: boolean }>(({ isRounded }) => ({
  backgroundColor: color.white,
  padding: '40px',
  borderRadius: (isRounded ?? false) ? '4px' : '0',
  h4: {
    fontFamily: 'Roboto',
    fontWeight: 400,
    fontSize: '32px',
    lineHeight: '38px',
    textAlign: 'center',
    color: color.black,
    marginBottom: '32px',
    marginTop: 0
  }
}))
